import { useCallback, useEffect, useState } from 'react';
import {
  getLessonVdoCipherOfflineOtp,
  getOfflineCourses,
  getOfflineVideos,
  removeOfflineCourse,
  removeOfflineVideos,
} from '../services/vdocipher';
import { mapOfflineCourses, mapOfflineVideos } from '../utils/vdocipherMappers';

export function useVdoCipherOffline({ courseId, enabled = true } = {}) {
  const [offlineCourses, setOfflineCourses] = useState([]);
  const [offlineVideos, setOfflineVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOfflineData = useCallback(async () => {
    if (!enabled) {
      setOfflineCourses([]);
      setOfflineVideos([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [coursesRes, videosRes] = await Promise.all([
        getOfflineCourses(),
        courseId ? getOfflineVideos({ course_id: courseId }) : Promise.resolve(null),
      ]);

      setOfflineCourses(mapOfflineCourses(coursesRes));
      setOfflineVideos(courseId ? mapOfflineVideos(videosRes) : []);
    } catch (e) {
      setError(e?.message || 'Failed to load offline downloads');
      setOfflineCourses([]);
      setOfflineVideos([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, enabled]);

  useEffect(() => {
    void fetchOfflineData().catch(() => {});
  }, [fetchOfflineData]);

  const downloadLesson = useCallback(async (lessonId) => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await getLessonVdoCipherOfflineOtp(lessonId);
      if (response?.success === false) {
        const message = response?.message || 'Failed to prepare offline download';
        setError(message);
        return { success: false, message };
      }
      await fetchOfflineData();
      return { success: true, data: response?.data ?? response };
    } catch (e) {
      const message = e?.message || 'Failed to download lesson';
      setError(message);
      return { success: false, message };
    } finally {
      setActionLoading(false);
    }
  }, [fetchOfflineData]);

  const removeLessons = useCallback(
    async (payload) => {
      setActionLoading(true);
      setError(null);

      try {
        const response = await removeOfflineVideos(payload);
        if (response?.success === false) {
          const message = response?.message || 'Failed to remove offline videos';
          setError(message);
          return false;
        }
        await fetchOfflineData();
        return true;
      } catch (e) {
        setError(e?.message || 'Failed to remove offline videos');
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [fetchOfflineData]
  );

  const removeCourseDownloads = useCallback(
    async (targetCourseId) => {
      if (!targetCourseId) return false;

      setActionLoading(true);
      setError(null);

      try {
        const response = await removeOfflineCourse(targetCourseId);
        if (response?.success === false) {
          const message = response?.message || 'Failed to remove course downloads';
          setError(message);
          return false;
        }
        await fetchOfflineData();
        return true;
      } catch (e) {
        setError(e?.message || 'Failed to remove course downloads');
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [fetchOfflineData]
  );

  return {
    offlineCourses,
    offlineVideos,
    loading,
    actionLoading,
    error,
    downloadLesson,
    removeLessons,
    removeCourseDownloads,
    refetch: fetchOfflineData,
  };
}
